import { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import communityAPI from '../api';
import {
  buildPaginationMeta,
  normalizeListResponse
} from '../api/normalizers';

const sanitizeFilters = (filters = {}) => {
  return Object.entries(filters).reduce((acc, [key, value]) => {
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return acc;
      }
      acc[key] = value;
      return acc;
    }

    if (value === undefined || value === null || value === '') {
      return acc;
    }

    acc[key] = value;
    return acc;
  }, {});
};

const createDedupeResolver = (dedupeKeyOption) => {
  if (typeof dedupeKeyOption === 'function') {
    return dedupeKeyOption;
  }

  if (typeof dedupeKeyOption === 'string') {
    return (item = {}) =>
      item?.[dedupeKeyOption] ?? item?.slug ?? item?.id ?? item?.uuid ?? null;
  }

  return (item = {}) => item?.slug ?? item?.id ?? item?.uuid ?? null;
};

const mergeUnique = (items, resolver) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  const merged = [];
  const indexMap = new Map();

  items.forEach((item) => {
    const key = resolver(item);

    if (key === null || key === undefined) {
      merged.push(item);
      return;
    }

    if (!indexMap.has(key)) {
      indexMap.set(key, merged.length);
      merged.push(item);
      return;
    }

    const existingIndex = indexMap.get(key);
    const existing = merged[existingIndex];
    merged[existingIndex] =
      typeof existing === 'object' ? { ...existing, ...item } : item;
  });

  return merged;
};

const flattenPages = (pages = []) => {
  if (!Array.isArray(pages) || pages.length === 0) {
    return [];
  }

  return pages.flatMap((page) => page?.data ?? []);
};

const defaultMeta = {
  count: 0,
  page: 1,
  pageSize: 0,
  totalPages: 1,
  hasNext: false,
  hasPrevious: false
};

const buildListParams = ({
  filters,
  page,
  pageSize,
  paginated
}) => {
  const params = { ...filters };

  if (paginated) {
    params.page = page;
    if (pageSize) {
      params.page_size = pageSize;
    }
  } else if (pageSize) {
    params.page_size = pageSize;
  }

  return params;
};

const fetchPostsPage = async ({
  filters,
  page,
  pageSize,
  paginated
}) => {
  const params = buildListParams({ filters, page, pageSize, paginated });
  const response = await communityAPI.posts.getAll(params);
  const normalized = normalizeListResponse(response.data);
  const meta = buildPaginationMeta({
    response: normalized,
    page: paginated ? page : 1,
    pageSize: paginated ? pageSize : normalized.results.length
  });

  return {
    data: normalized.results,
    meta
  };
};

export const usePosts = (filters = {}, config = {}) => {
  const {
    initialPage = 1,
    pageSize: initialPageSize = 12,
    paginated = true,
    skip = false,
    cache = true,
    infinite = false,
    dedupeKey: dedupeKeyOption,
    resetOnFilters
  } = config;

  const [page, setPageState] = useState(initialPage);
  const [pageSize, setPageSizeState] = useState(initialPageSize);

  const filtersKey = useMemo(() => JSON.stringify(filters || {}), [filters]);
  const sanitizedFilters = useMemo(
    () => sanitizeFilters(filters),
    [filtersKey]
  );

  const queryClient = useQueryClient();
  const dedupeResolver = useMemo(
    () => createDedupeResolver(dedupeKeyOption),
    [dedupeKeyOption]
  );
  const shouldResetOnFilters = resetOnFilters ?? infinite;

  const queryBaseKey = useMemo(
    () => [
      'community',
      'posts',
      { filters: sanitizedFilters, pageSize, paginated, infinite }
    ],
    [sanitizedFilters, pageSize, paginated, infinite]
  );

  const fetchPage = useCallback(
    (pageParam) =>
      fetchPostsPage({
        filters: sanitizedFilters,
        page: pageParam,
        pageSize,
        paginated
      }),
    [paginated, pageSize, sanitizedFilters]
  );

  const fetchCollection = useCallback(
    () =>
      fetchPostsPage({
        filters: sanitizedFilters,
        page: 1,
        pageSize,
        paginated: false
      }),
    [pageSize, sanitizedFilters]
  );

  const infiniteQuery = useInfiniteQuery({
    queryKey: [...queryBaseKey, 'infinite'],
    queryFn: ({ pageParam = initialPage }) => fetchPage(pageParam),
    initialPageParam: initialPage,
    getNextPageParam: (lastPage) =>
      lastPage?.meta?.hasNext ? lastPage.meta.page + 1 : undefined,
    getPreviousPageParam: (firstPage) =>
      firstPage?.meta?.hasPrevious && firstPage.meta.page > 1
        ? firstPage.meta.page - 1
        : undefined,
    enabled: !skip && paginated && infinite,
    staleTime: cache ? 30_000 : 0,
    gcTime: cache ? 300_000 : 0
  });

  const paginatedQuery = useQuery({
    queryKey: [...queryBaseKey, 'page', page],
    queryFn: () => fetchPage(page),
    enabled: !skip && paginated && !infinite,
    keepPreviousData: true,
    staleTime: cache ? 30_000 : 0,
    gcTime: cache ? 300_000 : 0
  });

  const collectionQuery = useQuery({
    queryKey: [...queryBaseKey, 'all'],
    queryFn: () => fetchCollection(),
    enabled: !skip && !paginated,
    staleTime: cache ? 30_000 : 0,
    gcTime: cache ? 300_000 : 0
  });

  const activeMeta = useMemo(() => {
    if (skip) {
      return defaultMeta;
    }

    if (paginated && infinite) {
      const pages = infiniteQuery.data?.pages ?? [];
      const first = pages[0]?.meta;
      const last = pages[pages.length - 1]?.meta;
      return {
        ...defaultMeta,
        ...(first || last || {})
      };
    }

    if (paginated) {
      return {
        ...defaultMeta,
        ...(paginatedQuery.data?.meta || {})
      };
    }

    return {
      ...defaultMeta,
      ...(collectionQuery.data?.meta || {})
    };
  }, [
    skip,
    paginated,
    infinite,
    infiniteQuery.data,
    paginatedQuery.data,
    collectionQuery.data
  ]);

  const rawPosts = useMemo(() => {
    if (skip) {
      return [];
    }

    if (paginated && infinite) {
      return flattenPages(infiniteQuery.data?.pages);
    }

    if (paginated) {
      return paginatedQuery.data?.data ?? [];
    }

    if (collectionQuery.data?.data) {
      return collectionQuery.data.data;
    }

    if (Array.isArray(collectionQuery.data)) {
      return collectionQuery.data;
    }

    return [];
  }, [
    skip,
    paginated,
    infinite,
    infiniteQuery.data,
    paginatedQuery.data,
    collectionQuery.data
  ]);

  const posts = useMemo(
    () => mergeUnique(rawPosts, dedupeResolver),
    [rawPosts, dedupeResolver]
  );

  const loading = useMemo(() => {
    if (skip) {
      return false;
    }

    if (paginated && infinite) {
      return infiniteQuery.isPending;
    }

    if (paginated) {
      return paginatedQuery.isPending;
    }

    return collectionQuery.isPending;
  }, [
    skip,
    paginated,
    infinite,
    infiniteQuery.isPending,
    paginatedQuery.isPending,
    collectionQuery.isPending
  ]);

  const error = useMemo(() => {
    if (skip) {
      return null;
    }

    if (paginated && infinite) {
      return infiniteQuery.error ?? null;
    }

    if (paginated) {
      return paginatedQuery.error ?? null;
    }

    return collectionQuery.error ?? null;
  }, [
    skip,
    paginated,
    infinite,
    infiniteQuery.error,
    paginatedQuery.error,
    collectionQuery.error
  ]);

  const setPage = useCallback((value) => {
    setPageState((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;
      return typeof next === 'number' && next > 0 ? next : 1;
    });
  }, []);

  const setPageSize = useCallback(
    (value) => {
      setPageSizeState((prev) => {
        const next = typeof value === 'function' ? value(prev) : value;
        return typeof next === 'number' && next > 0 ? next : initialPageSize;
      });
      if (paginated) {
        setPageState(initialPage);
      }
    },
    [initialPage, initialPageSize, paginated]
  );

  const invalidateList = useCallback(
    async () => {
      await queryClient.invalidateQueries({ queryKey: ['community', 'posts'] });
    },
    [queryClient]
  );

  const fetchPosts = useCallback(
    async (options = {}) => {
      if (skip) {
        return [];
      }

      const force = Boolean(options.force);

      if (force) {
        await invalidateList();
      }

      if (paginated && infinite) {
        if (options.append) {
          if (!infiniteQuery.hasNextPage) {
            const pages = infiniteQuery.data?.pages ?? [];
            return mergeUnique(flattenPages(pages), dedupeResolver);
          }
          const result = await infiniteQuery.fetchNextPage();
          const pages = result.data?.pages ?? infiniteQuery.data?.pages ?? [];
          return mergeUnique(flattenPages(pages), dedupeResolver);
        }
        const result = await infiniteQuery.refetch();
        const pages = result.data?.pages ?? infiniteQuery.data?.pages ?? [];
        return mergeUnique(flattenPages(pages), dedupeResolver);
      }

      if (paginated) {
        const targetPage = options.page ? Number(options.page) : page;
        if (targetPage !== page) {
          setPage(targetPage);
        }
        const queryKey = [...queryBaseKey, 'page', targetPage];
        const result = await queryClient.fetchQuery({
          queryKey,
          queryFn: () => fetchPage(targetPage)
        });
        return mergeUnique(result?.data ?? [], dedupeResolver);
      }

      const result = await queryClient.fetchQuery({
        queryKey: [...queryBaseKey, 'all'],
        queryFn: () => fetchCollection()
      });
      if (Array.isArray(result?.data)) {
        return mergeUnique(result.data, dedupeResolver);
      }
      return mergeUnique(result ?? [], dedupeResolver);
    },
    [
      skip,
      invalidateList,
      paginated,
      infinite,
      infiniteQuery,
      dedupeResolver,
      queryClient,
      queryBaseKey,
      fetchPage,
      page,
      setPage,
      fetchCollection
    ]
  );

  const loadMore = useCallback(
    async (options = {}) => {
      if (!paginated || !infinite) {
        return [];
      }
      if (!infiniteQuery.hasNextPage) {
        return [];
      }
      const result = await fetchPosts({ ...options, append: true });
      return result;
    },
    [paginated, infinite, infiniteQuery.hasNextPage, fetchPosts]
  );

  const createPostMutation = useMutation({
    mutationFn: (payload) => communityAPI.posts.create(payload),
    onSuccess: async () => {
      message.success('Post created successfully');
      await invalidateList();
      if (paginated) {
        setPage(initialPage);
      }
    }
  });

  const updatePostMutation = useMutation({
    mutationFn: ({ slug, data }) => communityAPI.posts.update(slug, data),
    onSuccess: async () => {
      message.success('Post updated successfully');
      await invalidateList();
    },
    onError: () => {
      message.error('Failed to update post');
    }
  });

  const deletePostMutation = useMutation({
    mutationFn: (slug) => communityAPI.posts.delete(slug),
    onSuccess: async () => {
      message.success('Post deleted successfully');
      await invalidateList();
    },
    onError: () => {
      message.error('Failed to delete post');
    }
  });

  const toggleLikeMutation = useMutation({
    mutationFn: (slug) => communityAPI.posts.toggleLike(slug),
    onMutate: async (slug) => {
      await queryClient.cancelQueries({ queryKey: ['community', 'posts'] });
      return { slug };
    },
    onError: () => {
      message.error('Failed to toggle like');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['community', 'posts'] });
    }
  });

  useEffect(() => {
    if (paginated) {
      setPageState(initialPage);
    }
  }, [initialPage, paginated]);

  useEffect(() => {
    if (shouldResetOnFilters && paginated) {
      setPageState(initialPage);
    }
  }, [shouldResetOnFilters, filtersKey, paginated, initialPage]);

  const totalPages = activeMeta.totalPages ?? 1;

  return {
    posts,
    loading,
    loadingMore: paginated && infinite ? infiniteQuery.isFetchingNextPage : false,
    error,
    fetchPosts,
    createPost: async (data) => {
      const response = await createPostMutation.mutateAsync(data);
      return response?.data;
    },
    updatePost: (slug, data) =>
      updatePostMutation.mutateAsync({ slug, data }).then((response) => response.data),
    deletePost: (slug) => deletePostMutation.mutateAsync(slug),
    toggleLike: async (slug) => {
      const response = await toggleLikeMutation.mutateAsync(slug);
      return response?.data;
    },
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount: activeMeta.count,
    totalPages,
    hasNext: paginated && infinite ? Boolean(infiniteQuery.hasNextPage) : Boolean(activeMeta.hasNext),
    hasPrevious: paginated && infinite ? Boolean(infiniteQuery.hasPreviousPage) : Boolean(activeMeta.hasPrevious),
    loadMore,
    refetch: fetchPosts
  };
};

export default usePosts;
